import { Injectable } from '@angular/core';
import { AdContent } from '@core/models/ad-content.model';
import { CalculateRequest } from '@core/models/calculate-request';
import { OrderAdDetailUpdate } from '@core/models/order-ad-detail-update.model';
import { OrderAdDetail } from '@core/models/order-ad-detail.model';
import { Order } from '@core/models/order.model';
import { Logger } from '@core/services/logger.service';
import { OrderViewModel } from '@order/models/order-view-model.model';
import { BackendOrderConverter } from '@order/services/backend-order-converter';
import { EditOrderConversionService } from './edit-order-conversion.service';

@Injectable({
	providedIn: 'root'
})
export class CalculatePriceRequestService {

	private readonly COMPONENT_NAME = "CalculatePriceRequestService";

	constructor (
		private _logger: Logger,
		private _editOrderConversionService: EditOrderConversionService,
		private _backendOrderConverter: BackendOrderConverter) { }

	createRequest(orderViewModel: OrderViewModel, needsRender: boolean): CalculateRequest {
		const request = new CalculateRequest();

		request.id = orderViewModel.id;
		request.render = needsRender;
		request.orderAdDetailUpdates = this.getOrderAdDetailsUpdateArray(orderViewModel);
		request.subscriberNumber = orderViewModel.subscriberNumber;

		if (orderViewModel.contentVM?.adContentCache?.length) {
			request.adContents = orderViewModel.contentVM.adContentCache;
		}
		else {
			const adContent = new AdContent();

			adContent.materialId = "";
			adContent.preview = "";
			adContent.columns = 0;
			adContent.depth = 0;
			adContent.width = 0;
			adContent.moduleLabel = "";
			adContent.lines = 0;
			adContent.words = 0;

			// the AdContentElements could be omitted in the request if render === false as these are only used for rendering
			if (request.render && orderViewModel.contentVM.style) {
				adContent.styleId = orderViewModel.contentVM.style.id;

				if (this._editOrderConversionService.isStyleMultifield(orderViewModel.contentVM.style)) {
					if (orderViewModel.contentVM.multiFieldEditorContent) {
						adContent.adContentElements = this._editOrderConversionService.createMultifieldContentElements(orderViewModel.contentVM.multiFieldEditorContent);
					}
					else {
						this._logger.error(this.COMPONENT_NAME, "createRequest(): the method is called when the multi-field content is empty. Look for a bug in the code.");
					}
				}
				else {
					adContent.adContentElements = this._editOrderConversionService.createSingleMultifieldContentElementsArray("maintext", orderViewModel.contentVM.adText, "");
				}
			}
			else {
				this._logger.error(this.COMPONENT_NAME, "createRequest(): the method is called when the Style is empty. Look for a bug in the code");
			}

			request.adContents = [adContent];
		}

		return request;
	}

	private getOrderAdDetailsUpdateArray(orderViewModel: OrderViewModel): OrderAdDetailUpdate[] {

		const orderAdDetailUpdateArray: OrderAdDetailUpdate[] = [];

		if (orderViewModel.adVM.issueDates && orderViewModel.adVM.issueDates.length > 0) {
			// issues are selected
			for (const issueDate of orderViewModel.adVM.issueDates) {
				const orderAdDetailUpdate = new OrderAdDetailUpdate();
				orderAdDetailUpdate.id = ""; // new
				orderAdDetailUpdate.issueDate = this._editOrderConversionService.getIssueAsISOString(issueDate);
				orderAdDetailUpdate.packageId = orderViewModel.adVM.package?.id;
				orderAdDetailUpdate.placementId = orderViewModel.adVM.placement?.id;

				orderAdDetailUpdateArray.push(orderAdDetailUpdate);
			}
		}
		else {
			// issues are not selected: create an OrderAdDetailUpdate without a date
			const orderAdDetailUpdate = new OrderAdDetailUpdate();
			orderAdDetailUpdate.id = "";
			orderAdDetailUpdate.issueDate = "";
			orderAdDetailUpdate.packageId = orderViewModel.adVM.package?.id;
			orderAdDetailUpdate.placementId = orderViewModel.adVM.placement?.id;

			orderAdDetailUpdateArray.push(orderAdDetailUpdate);
		}

		return orderAdDetailUpdateArray;
	}

	createCalculateDisplayOrderRequest(order: Order): CalculateRequest {

		const request = new CalculateRequest();
		request.id = order.orderHeader.id || "";
		request.render = false;
		request.advertiserId = order.orderHeader.advertiserId;
		request.otherPayerId = order.orderHeader.otherPayerId;
		request.subscriberNumber = "";

		request.orderAdDetailUpdates = order.orderAdDetails.map((adDetail: OrderAdDetail) => {
			const orderAdDetailUpdate = new OrderAdDetailUpdate();
			orderAdDetailUpdate.id = adDetail.id;
			orderAdDetailUpdate.packageId = adDetail.packageId;
			orderAdDetailUpdate.placementId = adDetail.placementId;
			orderAdDetailUpdate.issueDate = adDetail.issueDate;
			orderAdDetailUpdate.materialId = adDetail.materialId;

			// send either moduleCode OR depth and columns
			if (adDetail.moduleCode) {
				orderAdDetailUpdate.moduleCode = adDetail.moduleCode;
			}
			else {
				orderAdDetailUpdate.columns = adDetail.columns;
				orderAdDetailUpdate.depth = adDetail.depth;
			}

			return orderAdDetailUpdate;
		});

		// The BE can send empty adContents in some scenarios, like a Calc response
		request.adContents = order.adContents ? [...order.adContents] : [];

		request.orderServiceUpdates = this._backendOrderConverter.createOrderServiceUpdates(order.orderServices);

		return request;
	}
}
